import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  OctagonX, TriangleAlert, ClipboardPlus, Undo2, Gauge, User, Calendar,
  ShieldAlert, Info, Camera, ArrowRight, FileText,
} from "lucide-react";
import api from "../lib/api";
import { withCache } from "../lib/offlineStore";
import { useAuth } from "../context/AuthContext";
import { hasRole } from "../lib/roles";
import { StatusBadge, STATUS_META, statusEmoji } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { APP } from "../constants/testIds";

const fmtDate = (d, h) => (d ? `${d.split("-").reverse().join("/")}${h ? " à " + h : ""}` : "—");

export default function FrontDetail() {
  const { frontId } = useParams();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(params.get("tab") || "apercu");
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    setLoading(true);
    setErrorMsg("");
    withCache(`front_detail_${frontId}`, () => api.get(`/fronts/${frontId}`).then((r) => r.data))
      .then(({ data }) => setData(data))
      .catch((err) => {
        setData(null);
        setErrorMsg(err.response?.status === 403 ? "Accès refusé — ce front appartient à un autre site." : "Front introuvable.");
      })
      .finally(() => setLoading(false));
  }, [frontId]);

  const chartData = useMemo(() => (data?.chart_30j || []).map((c) => ({ ...c, dateLabel: c.date.split("-").slice(1).reverse().join("/") })), [data]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-slate-500 font-mono uppercase text-sm">Chargement du front...</div>;
  }

  if (!data) {
    return (
      <div data-testid="front-detail-error" className="min-h-[60vh] flex items-center justify-center text-red-400 font-mono uppercase text-sm text-center px-4">
        {errorMsg}
      </div>
    );
  }

  const { front, etat, anomalies, actions, status_changes: changes, inspections } = data;
  const status = etat.Statut_Global || "AUTORISEE";
  const canReactivate = hasRole(user, "RESPONSABLE", "ADMINISTRATEUR");
  const openAnomalies = anomalies.filter((a) => a.Statut === "OUVERTE");
  const openActions = actions.filter((a) => a.Statut !== "CLOTUREE");
  const photos = [
    ...anomalies.filter((a) => a.Photo_URL).map((a) => ({ url: a.Photo_URL, label: `Anomalie ${a.Anomalie_ID}` })),
    ...changes.filter((c) => c.Photo_URL).map((c) => ({ url: c.Photo_URL, label: `Changement ${c.Changement_ID}` })),
  ];
  const purgeGalleries = changes
    .map((c) => ({
      id: c.Changement_ID, date: c.Date_Heure, motif: c.Motif,
      avant: (c.Photos_Avant_URLs || "").split("|").filter(Boolean),
      apres: (c.Photos_Apres_URLs || "").split("|").filter(Boolean),
    }))
    .filter((g) => g.avant.length > 0 || g.apres.length > 0);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      {status === "SUSPENDUE" && (
        <div data-testid="suspension-banner" className="rounded-2xl border-2 border-red-600 bg-red-950/90 p-6 space-y-3 shadow-[0_0_30px_-5px_rgba(220,38,38,0.6)]">
          <div className="flex items-center gap-2 text-red-300 font-black text-xl uppercase tracking-tight">
            <OctagonX className="w-7 h-7" /> ⛔ Exploitation Suspendue
          </div>
          <p className="text-red-200 text-sm">{changes.find((c) => c.Nouveau_Statut === "SUSPENDUE")?.Motif || "Suspension en cours"}</p>
          <div className="flex flex-wrap gap-3 pt-1">
            <Button data-testid={APP.btnNewInspection} onClick={() => window.location.assign(`/inspection/new/${frontId}`)} className="bg-slate-100 text-red-900 hover:bg-white font-bold uppercase text-xs">
              <ClipboardPlus className="w-4 h-4 mr-1.5" /> Démarrer une inspection
            </Button>
            {canReactivate && (
              <Button data-testid={APP.btnReturnToService} onClick={() => window.location.assign(`/remise-en-service/${frontId}`)} variant="outline" className="border-red-400 text-red-200 font-bold uppercase text-xs">
                <Undo2 className="w-4 h-4 mr-1.5" /> Demander la remise en exploitation
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-500 mb-1">{front.Front_ID}</div>
          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-50 mb-3">{front.Front_Libelle}</h1>
          <StatusBadge status={status} testId="front-detail-status-badge" />
        </div>
        {status !== "SUSPENDUE" && (
          <Button data-testid={APP.btnNewInspection} onClick={() => window.location.assign(`/inspection/new/${frontId}`)} className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase text-xs">
            <ClipboardPlus className="w-4 h-4 mr-1.5" /> Démarrer une inspection
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: Gauge, label: "Score", value: etat.Dernier_Score !== "" ? `${etat.Dernier_Score}%` : "—" },
          { icon: User, label: "Dernier inspecteur", value: etat.Dernier_Inspecteur || "—" },
          { icon: Calendar, label: "Dernière purge", value: etat.Date_Derniere_Purge ? etat.Date_Derniere_Purge.split("-").reverse().join("/") : "Aucune" },
          { icon: ShieldAlert, label: "Anomalies critiques", value: etat.Nb_Anomalies_Critiques || 0 },
        ].map((k, i) => (
          <div key={i} className="rounded-xl border border-slate-800 bg-[#121820] p-4">
            <k.icon className="w-4 h-4 text-cyan-500 mb-2" />
            <div className="text-lg font-bold text-slate-100">{k.value}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">{k.label}</div>
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#121820] border border-slate-800 flex-wrap h-auto">
          <TabsTrigger data-testid={APP.tabFrontOverview} value="apercu">Aperçu</TabsTrigger>
          <TabsTrigger data-testid={APP.tabFrontInspections} value="inspections">Inspections</TabsTrigger>
          <TabsTrigger data-testid={APP.tabFrontAnomalies} value="anomalies">Anomalies ({openAnomalies.length})</TabsTrigger>
          <TabsTrigger data-testid={APP.tabFrontActions} value="actions">Actions ({openActions.length})</TabsTrigger>
          <TabsTrigger data-testid={APP.tabFrontPhotos} value="photos">Photos</TabsTrigger>
          <TabsTrigger data-testid={APP.tabFrontHistory} value="historique">Historique statut</TabsTrigger>
        </TabsList>

        <TabsContent value="apercu" className="space-y-6 pt-4">
          <div className="rounded-xl border border-slate-800 bg-[#121820] p-5">
            <h3 className="text-sm font-bold uppercase tracking-widest text-slate-300 mb-4">Évolution du score de conformité (30 jours)</h3>
            {chartData.length === 0 ? (
              <p className="text-sm text-slate-500">Aucune inspection sur les 30 derniers jours.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData}>
                  <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                  <XAxis dataKey="dateLabel" stroke="#64748b" fontSize={11} />
                  <YAxis domain={[0, 100]} stroke="#64748b" fontSize={11} />
                  <Tooltip
                    contentStyle={{ background: "#121820", border: "1px solid #283545", borderRadius: 8 }}
                    labelStyle={{ color: "#e2e8f0" }}
                    formatter={(v, n, p) => [`${v}% ${statusEmoji(p.payload.statut)}`, "Score"]}
                  />
                  <Line type="monotone" dataKey="score" stroke="#22D3EE" strokeWidth={2.5} dot={{ r: 4, fill: "#22D3EE" }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {purgeGalleries.length > 0 && (
            <div data-testid="purge-gallery-section" className="rounded-xl border border-slate-800 bg-[#121820] p-5 space-y-5">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-300">Galerie avant / après purge</h3>
              {purgeGalleries.map((g) => (
                <div key={g.id} data-testid={`purge-gallery-item-${g.id}`} className="space-y-2 border-t border-slate-800/60 pt-4 first:border-t-0 first:pt-0">
                  <div className="text-xs text-slate-500">{g.date ? new Date(g.date).toLocaleString("fr-FR") : ""} — {g.motif}</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-red-400 mb-1.5">Avant</div>
                      {g.avant.length === 0 ? (
                        <p className="text-xs text-slate-600">Aucune photo</p>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {g.avant.map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noreferrer" data-testid={`purge-photo-avant-${g.id}-${i}`} className="block aspect-square rounded-lg overflow-hidden border border-slate-800">
                              <img src={url} alt="avant purge" className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mb-1.5">Après</div>
                      {g.apres.length === 0 ? (
                        <p className="text-xs text-slate-600">Aucune photo</p>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {g.apres.map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noreferrer" data-testid={`purge-photo-apres-${g.id}-${i}`} className="block aspect-square rounded-lg overflow-hidden border border-slate-800">
                              <img src={url} alt="après purge" className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="inspections" className="space-y-3 pt-4">
          {inspections.length === 0 && <p className="text-sm text-slate-500">Aucune inspection enregistrée.</p>}
          {inspections.map((i) => (
            <div key={i.Inspection_ID} data-testid={`inspection-row-${i.Inspection_ID}`} className="rounded-xl border border-slate-800 bg-[#121820] p-4 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-sm font-semibold text-slate-100">{fmtDate(i.Date, i.Heure)} — {i.Inspecteur_Nom}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                Score {i.Score}% · Avant: {i.Statut_Front_Avant_Inspection} · Proposition: {i.Proposition_Statut_Apres_Inspection}
                {i.Numero_Tir && <span> · N° tir: <span className="text-cyan-400 font-semibold">{i.Numero_Tir}</span></span>}
              </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={i.Statut_Front_Apres_Validation} />
                <Link to={`/inspection/${i.Inspection_ID}/rapport`}>
                  <Button data-testid={`inspection-report-link-${i.Inspection_ID}`} size="sm" variant="outline" className="border-slate-700 text-xs">
                    <FileText className="w-3.5 h-3.5 mr-1" /> Rapport PDF
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="anomalies" className="space-y-3 pt-4">
          {anomalies.length === 0 && <p className="text-sm text-slate-500">Aucune anomalie enregistrée.</p>}
          {anomalies.map((a) => (
            <AnomalieRow key={a.Anomalie_ID} anomalie={a} onClosed={() => api.get(`/fronts/${frontId}`).then(({ data }) => setData(data))} />
          ))}
        </TabsContent>

        <TabsContent value="actions" className="space-y-3 pt-4">
          {actions.length === 0 && <p className="text-sm text-slate-500">Aucune action enregistrée.</p>}
          {actions.map((a) => (
            <ActionRow key={a.Action_ID} action={a} onUpdated={() => api.get(`/fronts/${frontId}`).then(({ data }) => setData(data))} />
          ))}
        </TabsContent>

        <TabsContent value="photos" className="pt-4">
          {photos.length === 0 ? (
            <p className="text-sm text-slate-500">Aucune photo enregistrée pour ce front.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {photos.map((p, idx) => (
                <a key={idx} href={p.url} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-slate-800 aspect-square relative group">
                  <img src={p.url} alt={p.label} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center text-xs text-white px-2 text-center transition-opacity">
                    <Camera className="w-3 h-3 mr-1" /> {p.label}
                  </div>
                </a>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="historique" className="space-y-3 pt-4">
          {changes.length === 0 && <p className="text-sm text-slate-500">Aucun changement de statut enregistré.</p>}
          {changes.map((c) => (
            <div key={c.Changement_ID} data-testid={`status-change-row-${c.Changement_ID}`} className="rounded-xl border border-slate-800 bg-[#121820] p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-100 flex-wrap">
                <StatusBadge status={c.Ancien_Statut} /> <ArrowRight className="w-4 h-4 text-slate-500" /> <StatusBadge status={c.Nouveau_Statut} />
              </div>
              <div className="text-xs text-slate-400 mt-2">{c.Date_Heure ? new Date(c.Date_Heure).toLocaleString("fr-FR") : ""}</div>
              <div className="text-sm text-slate-300 mt-1"><b>Motif:</b> {c.Motif}</div>
              <div className="text-xs text-slate-500 mt-1">Validé par {c.Valide_Par_Nom} ({c.Role_Validateur}){c.Remise_En_Exploitation === "TRUE" ? " · Remise en exploitation" : ""}</div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AnomalieRow({ anomalie: a, onClosed }) {
  const [busy, setBusy] = useState(false);
  const close = async () => {
    setBusy(true);
    try {
      await api.put(`/anomalies/${a.Anomalie_ID}/close`, { commentaire: "Clôturée" });
      onClosed();
    } finally {
      setBusy(false);
    }
  };
  const gravColor = a.Gravite === "CRITIQUE" ? "text-red-400 border-red-500/40 bg-red-500/10" : a.Gravite === "MAJEURE" ? "text-amber-400 border-amber-500/40 bg-amber-500/10" : "text-slate-400 border-slate-600 bg-slate-800/40";
  return (
    <div data-testid={`anomalie-row-${a.Anomalie_ID}`} className="rounded-xl border border-slate-800 bg-[#121820] p-4 flex items-center justify-between gap-3 flex-wrap">
      <div>
        <span className={`inline-block text-[10px] font-bold uppercase tracking-widest rounded-full border px-2 py-0.5 mr-2 ${gravColor}`}>{a.Gravite}</span>
        <span className="text-sm text-slate-200">{a.Description}</span>
        <div className="text-xs text-slate-500 mt-1">Créée le {a.Date_Creation} {a.Statut === "CLOTUREE" ? `· Clôturée le ${a.Date_Cloture}` : ""}</div>
      </div>
      {a.Statut === "OUVERTE" && (
        <Button data-testid={`close-anomalie-${a.Anomalie_ID}`} size="sm" disabled={busy} onClick={close} variant="outline" className="border-slate-700 text-slate-300 text-xs">
          Clôturer
        </Button>
      )}
    </div>
  );
}

function ActionRow({ action: a, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const close = async () => {
    setBusy(true);
    try {
      await api.put(`/actions/${a.Action_ID}`, { statut: "CLOTUREE" });
      onUpdated();
    } finally {
      setBusy(false);
    }
  };
  const overdue = a.Statut !== "CLOTUREE" && a.Echeance && new Date(a.Echeance) < new Date();
  const daysOverdue = overdue ? Math.floor((new Date() - new Date(a.Echeance)) / 86400000) : 0;
  const severe = overdue && daysOverdue > 5;
  return (
    <div data-testid={`action-row-${a.Action_ID}`} className={`rounded-xl border p-4 flex items-center justify-between gap-3 flex-wrap ${severe ? "border-red-600 bg-red-950/30" : "border-slate-800 bg-[#121820]"}`}>
      <div>
        <div className="text-sm text-slate-200">{a.Description}</div>
        <div className="text-xs text-slate-500 mt-1">
          Responsable: {a.Responsable} · Échéance: {a.Echeance?.split("-").reverse().join("/")}
          {overdue && (
            <span data-testid={`action-overdue-badge-${a.Action_ID}`} className={`font-bold ml-2 ${severe ? "text-red-200 bg-red-700/60 border border-red-500 rounded px-1.5 py-0.5" : "text-red-400"}`}>
              EN RETARD{severe ? ` (${daysOverdue}j)` : ""}
            </span>
          )}
        </div>
      </div>
      {a.Statut !== "CLOTUREE" ? (
        <Button data-testid={`close-action-${a.Action_ID}`} size="sm" disabled={busy} onClick={close} variant="outline" className="border-slate-700 text-slate-300 text-xs">
          Clôturer
        </Button>
      ) : (
        <span className="text-xs text-emerald-400 font-bold uppercase">Clôturée</span>
      )}
    </div>
  );
}
